import InfiniteScroll from "infinite-scroll";

interface ResolvedPath {
    pathOption: string | (() => string | null);
}

export default class InfiniteScrollController {
    private root: HTMLElement;
    private container: HTMLElement;
    // Typisierung bewusst als any, da das Package keine offiziellen TypeScript-Typen mitliefert
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private infScroll: any;
    private nextHref: string | null = null;

    static mount(): void {
        const roots = document.querySelectorAll<HTMLElement>(
            "[data-cm-infinite-scroll]"
        );

        if (roots.length === 0) {
            return;
        }

        roots.forEach((root) => {
            const container = root.querySelector<HTMLElement>(
                ".cm-infinite-scroll-content"
            );

            if (!container) {
                return;
            }

            new InfiniteScrollController(root, container);
        });
    }

    constructor(root: HTMLElement, container: HTMLElement) {
        this.root = root;
        this.container = container;

        const appendSelector =
            this.root.dataset.infiniteScrollAppend ?? ".textmedia-teaser";
        const buttonSelector =
            this.root.dataset.infiniteScrollButton ??
            "[data-cm-infinite-scroll-button]";
        const pathSelector =
            this.root.dataset.infiniteScrollPath ?? ".next a";
        const statusSelector =
            this.root.dataset.infiniteScrollStatus ?? ".page-load-status";

        const button = this.root.querySelector<HTMLElement>(buttonSelector);
        const statusContainer =
            this.root.querySelector<HTMLElement>(statusSelector);
        const isPagination = this.root.querySelectorAll(pathSelector).length > 0;

        if (!isPagination) {
            return;
        }

        if (button) {
            button.style.display = "flex";
        }

        const { pathOption } = this.resolvePathOption(pathSelector);
        const usesFunctionPath = typeof pathOption === "function";

        this.infScroll = new InfiniteScroll(this.container, {
            path: pathOption,
            append: appendSelector,
            button: buttonSelector,
            scrollThreshold: false,
            loadOnScroll: false,
            history: false,
            historyTitle: false,
            status: statusSelector,
            // Bei Function-Path haengt checkLastPage davon ab, ob im geladenen
            // Response noch ein Pagination-Link vorhanden ist. So markiert sich
            // die letzte Seite zuverlaessig auch dann, wenn die pathFn (noch)
            // einen Wert liefert.
            checkLastPage: usesFunctionPath ? pathSelector : true,
        });

        let firstLoadCompleted = false;

        this.infScroll.on("request", () => {
            if (button) {
                button.style.display = "none";
            }
            if (statusContainer) {
                statusContainer.style.display = "block";
            }
        });

        this.infScroll.on("load", (body: Document) => {
            if (statusContainer) {
                statusContainer.style.display = "none";
            }

            // Den naechsten Pagination-Link 1:1 aus dem gerade geladenen
            // Response uebernehmen. Bei TYPO3 enthaelt jeder Pagination-Link
            // einen cHash, der vom konkreten Wert von currentPage abhaengt.
            // Wuerden wir die URL selbst zusammensetzen, blieben Teile (z.B.
            // der cHash oder andere Query-Parameter) auf der initialen Seite
            // haengen und Folgeseiten lieferten 404 zurueck.
            if (usesFunctionPath) {
                this.refreshNextHrefFrom(body, pathSelector);
            }

            if (!firstLoadCompleted) {
                firstLoadCompleted = true;
                if (!this.infScroll.canLoad) {
                    return;
                }
                // Scroll-basiertes Laden auf der bestehenden Instanz aktivieren,
                // statt die Instanz zu zerstoeren und neu zu erstellen. Destroy/
                // Recreate wuerde den pageIndex zuruecksetzen und bei URL-kodierten
                // Pfaden die gleiche Seite nochmal laden.
                this.infScroll.options.scrollThreshold = 400;
                this.infScroll.options.loadOnScroll = true;
                this.infScroll.enableScrollWatch();
            }
        });

        this.infScroll.on("error", () => {
            if (button) {
                button.style.display = "none";
            }
            if (statusContainer) {
                statusContainer.style.display = "none";
            }
        });

        this.infScroll.on("last", () => {
            if (button) {
                button.style.display = "none";
            }
            if (statusContainer) {
                statusContainer.style.display = "none";
            }
        });
    }

    /**
     * Liefert den vollstaendigen Pagination-Link aus dem aktuellen DOM als
     * Function-Path. So vermeiden wir den unsicheren Regex-Ansatz, der die
     * Seitenzahl aus der URL parsed: Bei TYPO3-URLs wie
     * `?tx_news_pi1[currentPage]=2&cHash=...8ac19e95ae` matched die
     * Catch-All-Regex die letzte Ziffernfolge im cHash (z.B. `95`) statt der
     * Seitenzahl - das fuehrt zu kaputten URLs ab Seite 3.
     *
     * Nach jedem Load wird `nextHref` aus dem Response neu gesetzt, damit
     * der cHash immer zur jeweils naechsten Seite passt.
     */
    private resolvePathOption(pathSelector: string): ResolvedPath {
        const nextLink = document.querySelector(pathSelector);
        if (!nextLink) {
            return { pathOption: pathSelector };
        }

        const href = nextLink.getAttribute("href");
        if (!href) {
            return { pathOption: pathSelector };
        }

        this.nextHref = href;

        const controller = this;
        const pathFn = (): string | null => controller.nextHref;

        return { pathOption: pathFn };
    }

    /**
     * Aktualisiert den Naechste-Seite-Link aus dem gerade geladenen Response,
     * damit der naechste Request einen gueltigen cHash mitfuehrt.
     */
    private refreshNextHrefFrom(body: Document, pathSelector: string): void {
        const nextLink = body.querySelector(pathSelector);
        const href = nextLink?.getAttribute("href") ?? null;
        this.nextHref = href || null;
    }
}
